import { useTranslation } from 'react-i18next';

import {
  LuCheck,
  LuChevronDown,
  LuChevronRight,
  LuEllipsis,
  LuMail,
} from 'react-icons/lu';

import { useSidebarTeamSwitcher } from './useSidebarTeamSwitcher';
import { TeamMonogram } from '../../../pages/private/Team/components/shared/TeamMonogram/TeamMonogram';
import { SidebarRailTooltip } from '../../SidebarRailTooltip/SidebarRailTooltip';

interface SidebarTeamSwitcherProps {
  isOpen: boolean;
}

const MONOGRAM_SIZE_TRIGGER = 40;
const MONOGRAM_SIZE_DROPDOWN = 34;
const MONOGRAM_SIZE_CONTRACTED = 44;
const PENDING_INVITE_ICON_SIZE = 34;
const PENDING_INVITE_BADGE_OFFSET = -3;

const stylesheet = {
  headerDivider: 'border-t border-semantic-border mt-2 mb-1',
  container: 'relative w-full',
  triggerContracted: 'flex items-center justify-center',
  triggerBase:
    'w-full flex items-center gap-[11px] px-[11px] py-[9px] rounded-semantic border bg-semantic-bg-elevated text-left',
  triggerInteractive:
    'cursor-pointer hover:bg-semantic-bg-hover transition-colors',
  triggerOpen: 'border-semantic-text-accent',
  triggerClosed: 'border-semantic-border',
  triggerTextBlock: 'flex flex-col min-w-0 flex-1',
  triggerLabel:
    'text-[10.5px] font-bold tracking-[0.05em] uppercase text-semantic-text-tertiary',
  triggerName: 'text-[14.5px] font-bold text-semantic-text-primary truncate',
  chevronWrapper: 'flex-shrink-0 flex text-semantic-text-tertiary',
  dropdown:
    'absolute top-[calc(100%+8px)] left-0 z-50 w-full max-h-[460px] overflow-y-auto bg-semantic-bg-surface border border-semantic-border rounded-semantic shadow-xl p-[8px] flex flex-col',
  sectionLabel:
    'text-[11px] font-extrabold tracking-[0.06em] uppercase text-semantic-text-tertiary px-[10px] pt-[6px] pb-[8px]',
  teamButton:
    'w-full flex items-center gap-[11px] rounded-semantic px-[10px] py-[9px] text-left border-none cursor-pointer transition-colors',
  teamButtonDefault: 'bg-transparent hover:bg-semantic-bg-hover',
  teamButtonActive: 'bg-semantic-bg-badge',
  teamTextBlock: 'flex flex-col min-w-0 flex-1',
  teamName: 'text-[14px] font-bold text-semantic-text-primary truncate',
  teamSubtitle: 'text-[11.5px] text-semantic-text-tertiary mt-[1px]',
  checkWrapper: 'flex-shrink-0 flex text-semantic-text-accent',
  divider: 'h-[1px] bg-semantic-border mx-[6px] my-[8px]',
  pendingButton:
    'w-full flex items-center gap-[11px] rounded-semantic px-[10px] py-[9px] text-left border-none cursor-pointer bg-transparent hover:bg-semantic-bg-hover transition-colors',
  pendingIconWrapper:
    'relative flex-shrink-0 rounded-semantic bg-semantic-bg-badge text-semantic-text-accent grid place-items-center',
  pendingBadge:
    'absolute min-w-[16px] h-[16px] px-[4px] rounded-full bg-semantic-text-accent text-[10.5px] font-extrabold text-white grid place-items-center',
  pendingTextBlock: 'flex flex-col min-w-0 flex-1',
  pendingTitle: 'block text-[13.5px] font-bold text-semantic-text-primary',
  pendingSubtitle: 'block text-[11.5px] text-semantic-text-tertiary mt-[1px]',
  pendingChevron: 'flex-shrink-0 flex text-semantic-text-tertiary',
  manageButton:
    'w-full flex items-center gap-[9px] rounded-semantic px-[10px] py-[9px] text-[13px] font-semibold text-semantic-text-secondary border-none cursor-pointer bg-transparent hover:bg-semantic-bg-hover transition-colors',
  skeletonMonogram:
    'animate-pulse rounded-semantic bg-semantic-border flex-shrink-0',
  skeletonTextBlock: 'flex flex-col gap-[6px] min-w-0 flex-1',
  skeletonLabel:
    'animate-pulse h-[8px] w-[40px] rounded-full bg-semantic-border',
  skeletonName:
    'animate-pulse h-[10px] w-[80px] rounded-full bg-semantic-border',
};

export function SidebarTeamSwitcher({
  isOpen,
}: Readonly<SidebarTeamSwitcherProps>) {
  const { t } = useTranslation();
  const {
    isOpen: dropdownOpen,
    isLoading,
    activeTeamName,
    activeTeamColor,
    activeTeamId,
    teams,
    pendingInvites,
    isStatic,
    containerRef,
    handleToggle,
    handleSelectTeam,
    handleManageTeams,
    handlePendingInvitesClick,
    handleKeyDown,
  } = useSidebarTeamSwitcher();

  if (isStatic) {
    return null;
  }

  if (!isOpen) {
    const contractedTrigger = (
      <div className={stylesheet.triggerContracted}>
        {isLoading ? (
          <div
            className={stylesheet.skeletonMonogram}
            style={{
              width: MONOGRAM_SIZE_CONTRACTED,
              height: MONOGRAM_SIZE_CONTRACTED,
              borderRadius: 'var(--radius-semantic)',
            }}
          />
        ) : (
          <TeamMonogram
            name={activeTeamName}
            color={activeTeamColor}
            size={MONOGRAM_SIZE_CONTRACTED}
          />
        )}
      </div>
    );

    return (
      <>
        {!isLoading && activeTeamName ? (
          <SidebarRailTooltip label={activeTeamName}>
            {contractedTrigger}
          </SidebarRailTooltip>
        ) : (
          contractedTrigger
        )}
        <div className={stylesheet.headerDivider} />
      </>
    );
  }

  const triggerBorderClass = dropdownOpen
    ? stylesheet.triggerOpen
    : stylesheet.triggerClosed;
  const triggerClass = `${stylesheet.triggerBase} ${triggerBorderClass} ${stylesheet.triggerInteractive}`;

  return (
    <>
      <div
        ref={containerRef}
        className={stylesheet.container}
        role="presentation"
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          className={triggerClass}
          aria-label={t('common.sidebar.teamSwitcher.ariaLabel')}
          aria-expanded={dropdownOpen}
          onClick={handleToggle}
        >
          {isLoading ? (
            <>
              <div
                className={stylesheet.skeletonMonogram}
                style={{
                  width: MONOGRAM_SIZE_TRIGGER,
                  height: MONOGRAM_SIZE_TRIGGER,
                }}
              />
              <div className={stylesheet.skeletonTextBlock}>
                <div className={stylesheet.skeletonLabel} />
                <div className={stylesheet.skeletonName} />
              </div>
            </>
          ) : (
            <>
              <TeamMonogram
                name={activeTeamName}
                color={activeTeamColor}
                size={MONOGRAM_SIZE_TRIGGER}
              />
              <div className={stylesheet.triggerTextBlock}>
                <span className={stylesheet.triggerLabel}>
                  {t('common.sidebar.teamSwitcher.label')}
                </span>
                <span className={stylesheet.triggerName}>{activeTeamName}</span>
              </div>
              <span className={stylesheet.chevronWrapper}>
                <LuChevronDown />
              </span>
            </>
          )}
        </button>

        {dropdownOpen && (
          <div className={stylesheet.dropdown} role="listbox">
            <div className={stylesheet.sectionLabel}>
              {t('common.sidebar.teamSwitcher.switchTeam')}
            </div>

            {teams.map((team) => {
              const isActive = team.id === activeTeamId;
              const teamButtonClass = `${stylesheet.teamButton} ${isActive ? stylesheet.teamButtonActive : stylesheet.teamButtonDefault}`;
              const roleLabel = team.myRole
                ? t(`roles.${team.myRole}`)
                : undefined;
              const subtitle = roleLabel
                ? t('common.sidebar.teamSwitcher.membersAndRole', {
                    count: team.memberCount,
                    role: roleLabel,
                  })
                : String(team.memberCount);

              return (
                <button
                  key={team.id}
                  type="button"
                  className={teamButtonClass}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelectTeam(team.id)}
                >
                  <TeamMonogram
                    name={team.name}
                    color={team.hexColor}
                    size={MONOGRAM_SIZE_DROPDOWN}
                  />
                  <div className={stylesheet.teamTextBlock}>
                    <span className={stylesheet.teamName}>{team.name}</span>
                    <span className={stylesheet.teamSubtitle}>{subtitle}</span>
                  </div>
                  {isActive && (
                    <span className={stylesheet.checkWrapper}>
                      <LuCheck strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              );
            })}

            {pendingInvites?.length > 0 && (
              <>
                <div className={stylesheet.divider} />
                <button
                  type="button"
                  className={stylesheet.pendingButton}
                  onClick={handlePendingInvitesClick}
                >
                  <span
                    className={stylesheet.pendingIconWrapper}
                    style={{
                      width: PENDING_INVITE_ICON_SIZE,
                      height: PENDING_INVITE_ICON_SIZE,
                    }}
                  >
                    <LuMail />
                    <span
                      className={stylesheet.pendingBadge}
                      style={{
                        top: PENDING_INVITE_BADGE_OFFSET,
                        right: PENDING_INVITE_BADGE_OFFSET,
                      }}
                    >
                      {pendingInvites.length}
                    </span>
                  </span>
                  <span className={stylesheet.pendingTextBlock}>
                    <span className={stylesheet.pendingTitle}>
                      {t('common.sidebar.teamSwitcher.pendingInvitesCount', {
                        count: pendingInvites.length,
                      })}
                    </span>
                    <span className={stylesheet.pendingSubtitle}>
                      {t('common.sidebar.teamSwitcher.reviewAndRespond')}
                    </span>
                  </span>
                  <span className={stylesheet.pendingChevron}>
                    <LuChevronRight />
                  </span>
                </button>
              </>
            )}

            <div className={stylesheet.divider} />
            <button
              type="button"
              className={stylesheet.manageButton}
              onClick={handleManageTeams}
            >
              <LuEllipsis />
              {t('common.sidebar.teamSwitcher.manageTeams')}
            </button>
          </div>
        )}
      </div>
      <div className={stylesheet.headerDivider} />
    </>
  );
}
